import editor from 'shared/editor/utils'
import appGraphql, { getData } from 'api/app_graphql'
import { selectIsInNoteMode } from 'selectors/location'
import { oauthTokenSelector } from 'selectors/app'
import { doOpenNotePage, doOpenTicketPage } from 'actions/pages'

import { selectCurrentTicketId } from 'ducks/tickets/selectors/selectCurrentTicketId'
import { selectDraftByTicketId } from 'ducks/drafts2/selectors'
import { handleDraftChange } from 'ducks/drafts2/operations'
import { doUpdateEditorVisibility } from 'ducks/editor/operations'

// Copilot sends multi-paragraph replies as <p> blocks. The editor's content
// cleanup (groove-before-set-content-cleanup) rewrites <p> to margin-less <div>,
// collapsing the visual gap between paragraphs. We can't reliably keep <p>: when
// the composer is closed the HTML is stored in the draft and re-seeded on a fresh
// editor mount, where a per-call hint can't reach. So we bake the spacing into the
// content as an empty block ("blank line") between top-level blocks — it survives
// the cleanup unchanged and renders the same whether the composer was open or
// closed, on re-seed, and in the sent email.
const BLANK_LINE_BLOCK = '<div><br></div>'

// Mirror the editor's <p>→<div> cleanup so the stored/sent body never carries
// <p>. The reply is sent from the stored draft body (drafts2 doReply), not the
// live editor content, so the closed-composer path could otherwise send raw <p>.
// <p> keeps its intrinsic top/bottom margins in the recipient's email client (the
// composer CSS hides them while editing, so it looks fine), and those would
// compound with our blank line into double spacing. <div> has no default margin,
// so the only gap is the explicit blank line. The <p>'s own attributes/styles
// (e.g. margins) are dropped; inline formatting inside it is preserved.
const toSendSafeBlock = node => {
  if (node.tagName !== 'P') return node.outerHTML
  const div = document.createElement('div')
  div.innerHTML = node.innerHTML
  return div.outerHTML
}

// Lists keep their own default top/bottom margin, so a blank line next to one
// would double the gap. We skip the separator at list boundaries and let the
// list's margin space it.
const isListBlock = node => node.tagName === 'UL' || node.tagName === 'OL'

// Insert a blank line between top-level block elements so paragraph spacing
// survives — and produce a send-safe (<div>-normalized) representation, since the
// email is sent from this stored body. Whitespace-only text nodes (Copilot's HTML
// is pretty-printed, so there are newlines between tags) are ignored. No-op for
// empty/single-block input, or when there's meaningful loose text we'd otherwise
// drop by rebuilding from element nodes.
export const separateBlocksWithBlankLines = html => {
  if (!html || typeof document === 'undefined') return html
  const parser = document.createElement('div')
  parser.insertAdjacentHTML('afterbegin', html)
  const hasLooseText = Array.from(parser.childNodes).some(
    node => node.nodeType === 3 && node.textContent.trim() !== ''
  )
  const blocks = Array.from(parser.children)
  if (hasLooseText || blocks.length < 2) return html
  return blocks
    .map((node, i) => {
      const prev = blocks[i - 1]
      const separator =
        i > 0 && !isListBlock(prev) && !isListBlock(node)
          ? BLANK_LINE_BLOCK
          : ''
      return separator + toSendSafeBlock(node)
    })
    .join('')
}

/**
 * Insert HTML produced by the Copilot into the current ticket's composer,
 * as a public reply or an internal note. Opens the composer (Groove's can be
 * collapsed) and inserts into the active editor at the cursor (like Zendesk's
 * `ticket.editor.insert`). For a note it switches to the note page first, since
 * Groove's note is a separate route rather than a comment-type toggle.
 */
export const doInsertHelplyCopilotText = ({
  html: rawHtml,
  asNote = false,
}) => async (dispatch, getState) => {
  const ticketId = selectCurrentTicketId(getState())
  if (!ticketId || !rawHtml) return

  // Space the Copilot paragraphs so they survive the editor's <p>→<div> cleanup.
  const html = separateBlocksWithBlankLines(rawHtml)

  const draftType = asNote ? 'note' : 'reply'

  // Ensure a draft of this type exists before writing into it.
  await handleDraftChange(dispatch, draftType, undefined, ticketId, null, {
    touched: true,
  })

  const draft = selectDraftByTicketId(getState(), ticketId, draftType)

  if (asNote) {
    // "Insert as note": Groove's note is a separate page (not a comment-type
    // toggle like Zendesk), so make the note composer the active one and put the
    // content in. Open the composer in case it's collapsed.
    dispatch(doUpdateEditorVisibility(true))

    if (selectIsInNoteMode(getState())) {
      // Note composer already active — insert at the cursor and sync the draft.
      // `html` already carries blank-line separators so its paragraphs stay
      // spaced after the cleaner normalizes <p>→<div>.
      editor.insertContent(html, { focus: true })
      const content = editor.getContent()
      // When the composer is collapsed, TinyMCE isn't mounted yet so insert was a
      // no-op and getContent() returns false — append to the stored draft rather
      // than overwriting it with just the inserted html.
      const existing = (draft && draft.body) || ''
      // Default to appending to the stored draft (collapsed-composer case); use
      // the live editor content when TinyMCE is actually mounted.
      let body = existing ? `${existing}${html}` : html
      if (typeof content === 'string' && content) body = content
      handleDraftChange(
        dispatch,
        draftType,
        draft && draft.id,
        ticketId,
        null,
        {
          touched: true,
          body,
        }
      )
    } else {
      // Not in note mode: write the note draft, then switch to the note page —
      // the note composer mounts showing the draft body.
      const existing = (draft && draft.body) || ''
      handleDraftChange(
        dispatch,
        draftType,
        draft && draft.id,
        ticketId,
        null,
        {
          touched: true,
          body: existing ? `${existing}${html}` : html,
        }
      )
      dispatch(doOpenNotePage(ticketId))
    }
    return
  }

  // Reply: insert into the reply composer. Groove's note is a separate page and
  // the composer can be collapsed, so there are three cases:
  //   (a) on the reply page with TinyMCE mounted — insert at the cursor + sync;
  //   (b) on the reply page but collapsed (TinyMCE unmounted); or
  //   (c) on the note page.
  // For (b)/(c) `insertContent` can't reach the reply editor, and the composer
  // only seeds its body from the draft on a FRESH mount (a draft.body written
  // after it mounts is ignored), so we write the draft FIRST and then open the
  // reply page with `replying: true` — the TICKET_PAGE reducer flips the editor's
  // `isVisible`, remounting the composer showing the draft body. Toggling
  // visibility before writing (the old behavior) mounted the composer empty.
  const replyEditorMounted =
    !selectIsInNoteMode(getState()) && !!editor.getEditor()

  if (replyEditorMounted) {
    // Composer open — insert at the cursor and sync the draft from the live
    // content (handleDraftChange updates redux only and won't re-render the editor).
    // `html` carries blank-line separators so its paragraphs stay spaced after the
    // cleaner normalizes <p>→<div> (see the note branch above).
    editor.insertContent(html, { focus: true })
    const content = editor.getContent()
    const existing = (draft && draft.body) || ''
    let body = existing ? `${existing}${html}` : html
    if (typeof content === 'string' && content) body = content
    handleDraftChange(dispatch, draftType, draft && draft.id, ticketId, null, {
      touched: true,
      body,
    })
  } else {
    // Collapsed reply composer or on the note page: write the draft body first,
    // then open the reply page so the composer mounts showing it.
    const existing = (draft && draft.body) || ''
    handleDraftChange(dispatch, draftType, draft && draft.id, ticketId, null, {
      touched: true,
      body: existing ? `${existing}${html}` : html,
    })
    dispatch(doOpenTicketPage(ticketId, { replying: true }))
  }
}

/**
 * Replace the active composer (reply or note, whichever is open) with HTML from
 * the Copilot — the Format quick action. Mirrors Zendesk's
 * `client.set('ticket.comment.text', html)`: writes the draft body AND pushes
 * the content into the visible TinyMCE composer imperatively, since
 * `handleDraftChange` updates redux only and the editor guards against
 * re-syncing once it holds content/focus.
 */
export const doSetHelplyCopilotEditor = ({ html: rawHtml }) => async (
  dispatch,
  getState
) => {
  const state = getState()
  const ticketId = selectCurrentTicketId(state)
  if (!ticketId || typeof rawHtml !== 'string') return

  // Space the Copilot paragraphs so they survive the editor's <p>→<div> cleanup.
  const html = separateBlocksWithBlankLines(rawHtml)

  // Target whichever composer is active (route-driven note vs reply).
  const draftType = selectIsInNoteMode(state) ? 'note' : 'reply'

  await handleDraftChange(dispatch, draftType, undefined, ticketId, null, {
    touched: true,
  })

  const draft = selectDraftByTicketId(getState(), ticketId, draftType)

  handleDraftChange(dispatch, draftType, draft && draft.id, ticketId, null, {
    touched: true,
    body: html,
  })

  // Groove's composer can be collapsed (unlike Zendesk's); make sure it's open
  // so the result is visible. If it was closed, it mounts from the draft body
  // we just set.
  dispatch(doUpdateEditorVisibility(true))

  // Reflect the replace in the visible composer (same mechanism canned-reply
  // undo uses). `html` carries blank-line separators so its paragraphs stay
  // spaced after the cleaner normalizes <p>→<div>.
  editor.setContent(html, { focus: true })

  // setContent leaves the caret at the start; move it to the end.
  const tinyEditor = editor.getEditor()
  if (tinyEditor && tinyEditor.selection) {
    tinyEditor.selection.select(tinyEditor.getBody(), true)
    tinyEditor.selection.collapse(false)
  }
}

// The current agent's short-lived (~90s) grant — fetched fresh on every
// `request-grant`, so it's a standalone query (not cached like `helplyWidgetIdentifyToken`).
const helplyCopilotAuthGrantQuery = `
  query HelplyCopilotAuthGrant {
    me {
      id
      helplyCopilotAuthGrant
    }
  }
`

// Bound the grant fetch so a slow/unresponsive endpoint can't hang the copilot's
// auth flow forever (the iframe would sit waiting on `request-grant`). On timeout
// we resolve null → the iframe shows the auth error and the agent can retry.
// Must stay BELOW the iframe's own request-grant timer (GRANT_TIMEOUT_MS, 30s in
// groove-host.ts) so our definitive answer — grant or null — always arrives
// before the iframe gives up on its own.
const GRANT_FETCH_TIMEOUT_MS = 20000

/**
 * Fetch a short-lived, account-scoped grant the copilot iframe exchanges with
 * Helply for a session. Resolves to the grant string, or null.
 */
export const doFetchHelplyCopilotGrant = () => async (dispatch, getState) => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), GRANT_FETCH_TIMEOUT_MS)

  try {
    const token = oauthTokenSelector(getState())
    // `graphql()` takes no abort signal, so race it against the shared deadline
    // rather than aborting the request directly.
    const onTimeout = new Promise((resolve, reject) => {
      controller.signal.addEventListener('abort', () =>
        reject(new Error('Copilot grant fetch timed out'))
      )
    })
    const response = await Promise.race([
      appGraphql(token, helplyCopilotAuthGrantQuery),
      onTimeout,
    ])
    const data = getData(response)
    return (data && data.me && data.me.helplyCopilotAuthGrant) || null
  } catch (e) {
    return null
  } finally {
    clearTimeout(timeoutId)
  }
}
